import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../hooks/auth/useAuth'
import { ROUTES } from '../../constants/routes'
import { useToast } from '../../hooks/useToast'
import SocialAuthButton from '../../components/ui/SocialAuthButton'
import { authConfig, hasSocialAuth } from '../../config/auth'
import Logo from '../../components/ui/Logo'
import { AuthLayout } from '../../layouts/AuthLayout'
import { FormInput, FormButton } from '../../components/forms'
import { Alert } from '../../components/ui/Alert'
import { LegalFooter, Link as LinkUI } from '../../components/ui'
import { setLoginIntent } from '../../utils/loginIntent'

export const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signInWithEmail, signInWithProvider } = useAuth()
  const { showSuccess } = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Check for message in URL query params (e.g., from registration)
    const urlParams = new URLSearchParams(location.search)
    const messageParam = urlParams.get('message')

    if (messageParam) {
      // Show toast only once
      showSuccess(messageParam)
      // Clear the URL parameter after showing to prevent re-showing on refresh
      const newUrl = window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    } else if (location.state?.message) {
      // Check if there's a success message from navigation state
      showSuccess(location.state.message)
      // Clear the location state
      navigate(location.pathname, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state?.message])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    // Trim inputs to remove accidental spaces
    const trimmedEmail = email.trim()
    const trimmedPassword = password.trim()

    // Basic validation
    if (!trimmedEmail || !trimmedPassword) {
      setError('Please enter both email and password')
      setLoading(false)
      return
    }

    try {
      setLoginIntent() // Track user initiated login
      await signInWithEmail(trimmedEmail, trimmedPassword)
      navigate('/')
    } catch (error) {
      console.error('Login error:', error)
      // Provide more user-friendly error messages
      if (error.message === 'Invalid login credentials') {
        setError('Invalid email or password. Please check your credentials and try again.')
      } else if (error.message.includes('Email not confirmed')) {
        setError('Please confirm your email address before signing in.')
      } else {
        setError(error.message || 'An error occurred during sign in')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSocialAuth = async (provider) => {
    setError(null)
    setLoading(true)
    try {
      setLoginIntent() // Track OAuth login intent
      await signInWithProvider(provider)
      // The OAuth flow will redirect automatically
    } catch (error) {
      console.error('Social auth error:', error)
      let errorMessage = `Failed to sign in with ${provider}.`

      if (error.message?.includes('not active')) {
        errorMessage = `${provider} login is not properly configured. Please contact support.`
      } else if (error.message?.includes('unauthorized')) {
        errorMessage = `You are not authorized to use ${provider} login.`
      }

      setError(errorMessage)
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Link to={ROUTES.HOME}>
        <Logo className="text-white mb-12" />
      </Link>

      <div className="w-full max-w-md">
        {error && <Alert type="error" message={error} />}

        {authConfig.email.enabled && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormInput
              id="email"
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={loading}
              autoComplete="email"
            />

            <FormInput
              id="password"
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              disabled={loading}
              autoComplete="current-password"
            />

            <FormButton type="submit" loading={loading}>
              Sign In
            </FormButton>
          </form>
        )}

        {authConfig.email.enabled && hasSocialAuth() && (
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dark-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-dark-primary text-gray-500">or</span>
            </div>
          </div>
        )}

        {hasSocialAuth() && (
          <div className="space-y-3">
            {authConfig.google.enabled && (
              <SocialAuthButton provider="google" onClick={handleSocialAuth} />
            )}
            {authConfig.facebook.enabled && (
              <SocialAuthButton provider="facebook" onClick={handleSocialAuth} />
            )}
          </div>
        )}

        <div className="mt-8 text-center text-sm text-gray-400">
          Don&apos;t have an account?{' '}
          <LinkUI to={ROUTES.REGISTER} variant="white" className="font-medium">
            Create one
          </LinkUI>
        </div>

        <div className="mt-4 text-center">
          <LinkUI to={ROUTES.HOME} variant="secondary" size="sm" underline>
            Continue without signing in
          </LinkUI>
        </div>

        <LegalFooter prefix="By signing in, you agree to our" className="mt-4" />
      </div>
    </AuthLayout>
  )
}
